import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import debounce from "lodash/debounce";
import PermissionService from "service/permissionService";
import { OrganisationPermissions } from "types";
import { useAppContext } from "context/appContext";
import { storeSystemPermissions } from "store/actions";
import { HttpStatus } from "constant";
import { DefaultPermissions } from "components/organizations/PermissionList";

export function usePermissions(orgId?: string) {
    const [permissions, setPermissions] =
        useState<OrganisationPermissions>(DefaultPermissions);
    const [isLoaded, setIsLoaded] = useState(false);
    const {
        storeData: {
            organization: { currentOrgId },
            systemPermissions,
        },
        storeDispatchActions,
    } = useAppContext();

    useEffect(() => {
        const abortController = new AbortController();
        const _orgId = orgId || currentOrgId;
        if (orgId === currentOrgId && systemPermissions.loaded) {
            setPermissions(systemPermissions.permissions);
            setIsLoaded(systemPermissions.loaded);
        } else {
            !systemPermissions.loaded &&
                _orgId &&
                PermissionService.getDefaultPermissions(_orgId).then(
                    ({ status, data }) => {
                        if (abortController.signal.aborted) {
                            return;
                        }

                        if (status === HttpStatus.OK) {
                            storeDispatchActions(
                                storeSystemPermissions({
                                    loaded: true,
                                    permissions: data,
                                })
                            );

                            globalThis.window.setAuthorizationConfig(data);
                            setPermissions(data);
                            setIsLoaded(true);
                        }
                    }
                );
        }

        return () => {
            abortController.abort();
        };
    }, [systemPermissions, currentOrgId, orgId, storeDispatchActions]);

    return { permissions, isLoaded };
}

export function useDebounce(handle: Function) {
    return useMemo(
        () => debounce((...args: any) => handle(...args), 300),
        [handle]
    );
}

/**
 * Hook for throttling button clicks with visual feedback (disabled state).
 * Each button is identified by a unique key and has its own independent throttle timer.
 * Timers are automatically cleaned up when the component unmounts to prevent memory leaks.
 *
 * @param delay - Throttle delay in milliseconds (default: 2000ms)
 * @returns Object with throttle function and isThrottled checker
 *
 * @example
 * const { throttle, isThrottled } = useButtonThrottle(2000);
 *
 * <Button
 *   onClick={() => throttle('forward-btn', () => handleForward())}
 *   disabled={isThrottled('forward-btn')}
 * >
 *   Forward
 * </Button>
 */
export function useButtonThrottle(delay: number = 2000) {
    const [throttledButtons, setThrottledButtons] = useState<Set<string>>(
        new Set()
    );
    const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

    // Cleanup all timers on unmount
    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            timers.forEach((timerId) => clearTimeout(timerId));
            timers.clear();
        };
    }, []);

    const throttle = useCallback(
        (key: string, callback: () => void) => {
            if (throttledButtons.has(key)) return;

            callback();

            setThrottledButtons((prev) => new Set(prev).add(key));

            const timerId = setTimeout(() => {
                setThrottledButtons((prev) => {
                    const next = new Set(prev);
                    next.delete(key);
                    return next;
                });
                timersRef.current.delete(key);
            }, delay);

            timersRef.current.set(key, timerId);
        },
        [throttledButtons, delay]
    );

    const isThrottled = useCallback(
        (key: string) => throttledButtons.has(key),
        [throttledButtons]
    );

    return { throttle, isThrottled };
}
